import { useFormik } from 'formik';
import Button from '../../components/button/Button';
import Card from '../../components/card/Card';
import DropDown from '../../components/dropdown/DropDown';
import Input from '../../components/input/Input';
import H from '../../components/texts/h/H';
import P from '../../components/texts/p/P';
import { formatCurrency } from '../../utils/Masks';
import { useEffect, useMemo, useRef, useState } from 'react';
import Icon from '../../components/icon/Icon';
import Span from '../../components/texts/span/Span';
import { getProductLines, getCreditTypes } from '../../services/ClientService';
import { useClientTheme } from '../../context/ThemeContext';
import { useConfig } from '../../context/ConfigContext';
import DatePicker from '../../components/datepicker/DatePicker';
import {
  amount,
  applicationData,
  clientData,
  signersData,
} from '../../types/interfaces';
import Modal from '../../components/modal/modal';
import Spinner from '../../components/Spinner/spinner';
import Toggle from '../../components/toggle/Toggle';
import { CURRENCY_CODES } from '../../utils/Constants';
import {
  isApprovedStatus,
  isCommitteeStatus,
  isFinishedStatus,
  isInstrumentationStatus,
  isRejectedStatus,
} from '../../utils/applicationStatusUtils';
import TextArea from '../../components/textarea/TextArea';
import useStatus from '../../hooks/useStatus';
import useRole from '../../hooks/useRole';
import Signers from '../common/Signers';
import { toNumber } from '../../utils/Utils';
import { sumApplicationTotalsByCurrency } from '../../utils/applicationTotals';
import { resolveSignersHasErrors } from '../../utils/applicationSignersHasErrors';
import {
  ACTIVO_SIN_COTIZACION_LABEL,
  assetHasQuotation,
  buildOriginationCurrencyDropdownItems,
  filterActivoSelectableAssets,
  hasHistoricUsdProducts,
  hasQuotatedSaleValue,
  isUsdHistoricLocked,
  isUsdSelectableForOriginationCurrencyDropdown,
  resolveLocalCurrencyCode,
  resolveUsdAvailable,
  USD_HISTORIC_LOCK_BANNER_TEXT,
} from '../../utils/quotationAssets';
import {
  filterCreditTypesByManagement,
  getManagementAssetRules,
  getMissingQuotationMessages,
  isAssetOptionEnabled,
  pickDefaultAssetCode,
  resolveManagementCode,
  shouldHidePoliticalCreditType,
} from '../../utils/managementAssetRules';
import {
  defaultSelectableProductLineCode,
  isSelectableProductLineCode,
  parseSelectableProductLines,
  productLineDropdownTitle,
  type SelectableProductLine,
} from '../../utils/productLines';
import {
  SYNDICATED_CHECKBOX_HELPER_TEXT,
  LATE_SYNDICATION_HELPER_TEXT,
  buildOperacionesPublicasLine,
  canMarkSyndicated,
  canShowSyndicatedCheckbox,
} from '../../utils/syndicatedPublicOperations';
import { useManagements } from '../../hooks/useManagements';
import { NumericFormat } from 'react-number-format';
import Checkbox from '../../components/checkBox/CheckBox';

interface ApplicationProps {
  applications: any;
  cuit: string;
  id: string | undefined;
  onSave: Function;
  applicationStatus: number | string;
  onPreviousClick: Function;
  isSaving: boolean;
  manualTotalAmounts: boolean;
  requestedAmounts: amount[];
  reason?: string;
  review?: string;
  extraComment?: string;
  hideInfo?: boolean;
  allowCommitteeEdit?: boolean;
  onChange?: Function;
  clientData: clientData;
  signers: signersData[];
  isSyndicated?: boolean;
}

const Application: React.FC<ApplicationProps> = ({
  cuit,
  id,
  onSave,
  applications,
  applicationStatus,
  onPreviousClick,
  isSaving,
  manualTotalAmounts,
  requestedAmounts,
  reason,
  review,
  extraComment,
  hideInfo = false,
  allowCommitteeEdit = false,
  onChange,
  clientData,
  signers,
  isSyndicated: isSyndicatedProp = false,
}) => {
  const { clientTheme } = useClientTheme();
  const { isApproved, isRejected, isInCommittee, isFinished, isInInstrumentation } = useStatus({
    applicationStatus,
  });
  const { isCommercial, isComite, isInstrumentator, isProducer } = useRole();
  
  const config = useConfig();
  const isApplicationEffectiveDateEnabled = config.isApplicationEffectiveDateEnabled ?? false;
  const isApplicationCreditTypeEnabled = config.isApplicationCreditTypeEnabled ?? false;
  const isQuotationsEnabled = config.isQuotationsEnabled ?? false;
  const isUruguayEnabled = config.isUruguayEnabled ?? false;
  const isManagementSelectionEnabled = config.isManagementSelectionEnabled ?? false;
  const { managements } = useManagements(isManagementSelectionEnabled);
  const managementCode = resolveManagementCode(clientData?.management_id, managements);
  const managementRules = useMemo(
    () => getManagementAssetRules(managementCode),
    [managementCode]
  );
  const localCurrencyCode = resolveLocalCurrencyCode(config);
  const clientDataAny = clientData as
    | (clientData & {
        risk_analysis?: unknown;
        committee_approval_history?: unknown[];
      })
    | undefined;
  const allowMarkSyndicated = canMarkSyndicated({
    riskAnalysis: clientDataAny?.risk_analysis,
    products: (applications ?? clientDataAny?.products ?? []) as Array<{
      source?: string | null;
    }>,
    currentStatusCode: clientDataAny?.status?.code,
    committeeApprovalHistory: clientDataAny?.committee_approval_history,
  });
  const applicationIdNum = id ? parseInt(id, 10) : 0;
  const hasApplicationId = Number.isFinite(applicationIdNum) && applicationIdNum > 0;
  const showSyndicatedCheckbox = canShowSyndicatedCheckbox({
    isSyndicatedPublicOperationsEnabled: config.isSyndicatedPublicOperationsEnabled,
    roles: { isCommercial, isComite, isInstrumentator, isProducer },
    hasApplicationId,
    syndicationEligibleAtCreate: clientDataAny?.syndication_eligible_at_create,
    isSyndicated: !!isSyndicatedProp,
    canMark: allowMarkSyndicated,
  });
  const [isSyndicated, setIsSyndicated] = useState<boolean>(!!isSyndicatedProp);
  const [productLinesSnapshot, setProductLinesSnapshot] = useState<applicationData[] | null>(
    null
  );
  const [applicationInfo, setApplicationInfo] = useState<applicationData[]>([
    {
      id: 0,
      product_line_code: '',
      amount: {
        currency_code: localCurrencyCode,
        value: 0,
      },
      has_assets: false,
    },
  ]);
  const [_isShouldContinue, setIsShouldContinue] = useState<boolean>(false);
  const [totalArs, setTotalArs] = useState<number>(0);
  const [totalUsd, setTotalUsd] = useState<number>(0);
  const [productLines, setProductLines] = useState<SelectableProductLine[]>();
  const [creditTypes, setCreditTypes] = useState<any>();
  const [isDeleteApplicationOpen, setIsdeleteApplication] =
    useState<boolean>(false);
  const [productToDelete, setProductToDelete] = useState<number>(0);
  const [_modalText, setModalText] = useState<string>('');
  const [summarizeAmounts, setsummarizeAmounts] =
    useState<boolean>(!manualTotalAmounts);
  const [hasErrors, setHasErrors] = useState<boolean>(true);
  const signersHasErrors = resolveSignersHasErrors(isUruguayEnabled, hasErrors);
  const [clientInfo, setClientInfo] = useState<clientData>(clientData);
  const [signersUploadFunction, setSignersUploadFunction] = useState<(() => Promise<void>) | null>(null);
  const [isUploadingSignersFiles, setIsUploadingSignersFiles] = useState<boolean>(false);
  const [availableAssets, setAvailableAssets] = useState<any[]>([]);
  /** null: cargando o cotizaciones off; false/true: mostrar USD en Moneda (cotización + asset USD seleccionable) */
  const [usdQuotationAvailable, setUsdQuotationAvailable] = useState<boolean | null>(null);
  const quotationsDataLoadedRef = useRef(false);
  const [manualAmounts, setManualAmounts] = useState<amount[]>([
    {
      currency_code: localCurrencyCode,
      value: 0,
    },
    {
      currency_code: CURRENCY_CODES.usd,
      value: 0,
    },
  ]);

  const handleInputChange = (currencyCode: string, value: number | string) => {
    setManualAmounts((prev) =>
      prev.map((amount) =>
        amount.currency_code === currencyCode
          ? {
              ...amount,
              value: value === '' ? '' : toNumber(value),
            }
          : amount
      )
    );
  };
  useEffect(() => {
    if (!productLines) {
      getProductLines().then((response) => {
        if (response) {
          setProductLines(parseSelectableProductLines(response.data));
        }
      });
    }
    if (!creditTypes) {
      getCreditTypes().then((response) => {
        if (response) {
          setCreditTypes(response.data);
        }
      });
    }
  }, [productLines, creditTypes]);

  const visibleCreditTypes = useMemo(
    () => filterCreditTypesByManagement(creditTypes, managementRules),
    [creditTypes, managementRules]
  );

  const creditTypeItems = visibleCreditTypes.map((creditType: any) => ({
    label: creditType.name,
    disabled: false,
  }));

  const getSelectedCreditType = () => {
    const creditType = visibleCreditTypes.find(
      (ct: any) =>
        ct.id === applicationDataFormik.values.credit_type_id ||
        ct.name === applicationDataFormik.values.credit_type_name
    );
    return creditType?.name || 'Seleccionar';
  };

  const handleCreditTypeChange = (selectedLabel: string) => {
    const creditType = visibleCreditTypes.find((ct: any) => ct.name === selectedLabel);
    if (creditType) {
      applicationDataFormik.setFieldValue('credit_type_id', creditType.id);
      applicationDataFormik.setFieldValue('credit_type_name', creditType.name);
    }
  };

  const validateProductData = (values: applicationData[]) => {
    const errors: any[] = [];

    values.forEach(({ amount, has_assets, asset_code, asset_count, product_line_code }, index) => {
      const productErrors: any = {};
      if (Array.isArray(productLines) && productLines.length > 0) {
        if (!isSelectableProductLineCode(productLines, product_line_code)) {
          productErrors.product_line_code = 'Seleccioná un producto habilitado';
        }
      }
      if (!amount.value) {
        productErrors.amount = "Debes completar el campo 'Monto'";
      }
      if (amount.value === 0) {
        productErrors.amount = 'El monto debe ser mayor a 0';
      }
      
      // Validaciones para activos si la funcionalidad está habilitada
      if (isQuotationsEnabled && has_assets) {
        if (!asset_code) {
          productErrors.asset_code = "Debes seleccionar un activo";
        }
        if (!asset_count || asset_count === 0) {
          productErrors.asset_count = "La cantidad debe ser mayor a 0";
        }
        
        // Verificar si el activo tiene cotización
        if (asset_code) {
          const selectedAsset = Array.isArray(availableAssets) ?
            availableAssets.find((item: any) => item.code === asset_code) : null;
          if (selectedAsset && !assetHasQuotation(selectedAsset)) {
            productErrors.amount = "El activo no tiene cotización";
          }
        }
      }

      if (isQuotationsEnabled && managementRules) {
        const missing = getMissingQuotationMessages({
          rules: managementRules,
          assetCode: asset_code,
          currencyCode: amount.currency_code,
          assets: availableAssets,
        });
        if (missing.length > 0) {
          productErrors.asset_code = missing[0];
        }
        if (
          asset_code &&
          !managementRules.assetCodes.includes(String(asset_code))
        ) {
          productErrors.asset_code = 'Activo no permitido para la Gerencia seleccionada';
        }
      }
      
      if (Object.keys(productErrors).length > 0) {
        errors[index] = productErrors;
      }

      const hasErrors = errors.some((error) => Object.keys(error).length > 0);
      setIsShouldContinue(!hasErrors);
    });
    return errors;
  };

  const productFormik = useFormik({
    initialValues: applications || applicationInfo,
    validate: validateProductData,
    validateOnMount: true,
    enableReinitialize: true,
    onSubmit: (values) => {},
  });

  const usdAvailable = useMemo(
    () =>
      resolveUsdAvailable(
        {
          isQuotationsEnabled,
          flagUsdCurrencyEnabled: config.flagUsdCurrencyEnabled,
        },
        usdQuotationAvailable
      ),
    [isQuotationsEnabled, config.flagUsdCurrencyEnabled, usdQuotationAvailable]
  );

  const hasHistoricUsd = useMemo(
    () => hasHistoricUsdProducts(productFormik.values),
    [productFormik.values]
  );

  const usdHistoricLocked = useMemo(
    () => isUsdHistoricLocked(usdAvailable, hasHistoricUsd),
    [usdAvailable, hasHistoricUsd]
  );

  /** When management rules apply, USD requires quotation; else keep prior UY override. */
  const currencyUsdAvailable =
    managementRules != null
      ? usdAvailable === true
      : isUruguayEnabled
        ? true
        : usdAvailable;

  const hasQuotationConfigErrors = useMemo(() => {
    if (!isQuotationsEnabled || !managementRules) return false;
    return productFormik.values.some(
      (p) =>
        getMissingQuotationMessages({
          rules: managementRules,
          assetCode: p.asset_code,
          currencyCode: p.amount?.currency_code,
          assets: availableAssets,
        }).length > 0
    );
  }, [isQuotationsEnabled, productFormik.values, managementRules, availableAssets]);

  const quotationWarningMessages = useMemo(() => {
    if (!isQuotationsEnabled || !managementRules) return [] as string[];
    const all = productFormik.values.flatMap((p) =>
      getMissingQuotationMessages({
        rules: managementRules,
        assetCode: p.asset_code,
        currencyCode: p.amount?.currency_code,
        assets: availableAssets,
      })
    );
    // Also surface disabled options that block adding products (any missing base quote for allowed assets / moneda)
    const forMoneda = getMissingQuotationMessages({
      rules: managementRules,
      assetCode: '',
      currencyCode: localCurrencyCode,
      assets: availableAssets,
    });
    return [...new Set([...all, ...forMoneda])];
  }, [
    isQuotationsEnabled,
    managementRules,
    productFormik.values,
    availableAssets,
    localCurrencyCode,
  ]);

  useEffect(() => {
    if (productLines?.length) {
      void productFormik.validateForm();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- revalidar al cargar catálogo habilitado
  }, [productLines, managementRules, availableAssets]);

  useEffect(() => {
    if (!isQuotationsEnabled) {
      quotationsDataLoadedRef.current = false;
      setUsdQuotationAvailable(null);
      return;
    }
    if (quotationsDataLoadedRef.current) return;
    quotationsDataLoadedRef.current = true;

    const headers = {
      Authorization: `Bearer ${localStorage.getItem('token')}`,
    };

    Promise.all([
      fetch('/v1/private/applications/quotations', { headers }).then((res) =>
        res.json()
      ),
      fetch('/v1/private/applications/quotations/USD?page=0&size=1', {
        headers,
      }).then((res) => (res.ok ? res.json() : null)),
    ])
      .then(([data, usdPage]) => {
        const assets = Array.isArray(data) ? data : data?.data || [];
        setAvailableAssets(assets);
        setUsdQuotationAvailable(
          isUsdSelectableForOriginationCurrencyDropdown(usdPage, assets)
        );

        if (!applications || applications.length === 0) {
          const defaultCode = pickDefaultAssetCode(managementRules, assets);
          if (defaultCode) {
            const plCode = defaultSelectableProductLineCode(productLines ?? []);
            const defaultProduct = {
              id: 0,
              product_line_code: plCode,
              amount: {
                currency_code: localCurrencyCode,
                value: 0,
              },
              has_assets: true,
              asset_code: defaultCode,
            };
            productFormik.setValues([defaultProduct]);
            setApplicationInfo([defaultProduct]);
          }
        }
      })
      .catch((err) => {
        console.error('Error loading quotations:', err);
        setAvailableAssets([]);
        setUsdQuotationAvailable(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- productFormik estable; evitar re-fetch
  }, [isQuotationsEnabled, applications, productLines, creditTypes]);

  useEffect(() => {
    if (!isQuotationsEnabled || usdQuotationAvailable !== false || usdHistoricLocked) return;
    productFormik.values.forEach((p, index) => {
      if (!p.asset_code && p.amount.currency_code === CURRENCY_CODES.usd) {
        productFormik.setFieldValue(
          `[${index}].amount.currency_code`,
          localCurrencyCode
        );
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- productFormik estable; evitar bucle al incluir values
  }, [usdQuotationAvailable, isQuotationsEnabled, usdHistoricLocked, localCurrencyCode]);

  const applicationDataFormik = useFormik({
    initialValues: {
      reason: reason,
      review: review,
      extraComment: extraComment,
      effective_date: clientData?.effective_date || '',
      credit_type_id: clientData?.credit_type_id || null,
      credit_type_name: clientData?.credit_type_name || '',
    },

    enableReinitialize: true,
    onSubmit: (values) => {},
  });

  useEffect(() => {
    if (!shouldHidePoliticalCreditType(managementRules)) return;
    if (applicationDataFormik.values.credit_type_name === 'Político') {
      applicationDataFormik.setFieldValue('credit_type_id', null);
      applicationDataFormik.setFieldValue('credit_type_name', '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [managementRules]);

  useEffect(() => {
    updateTotals();
    productFormik.validateForm();
    onChange &&
      onChange({
        applications: productFormik.values,
        manualTotalAmounts: !summarizeAmounts,
        requestedAmounts: manualAmounts,
        is_syndicated: isSyndicated,
      });
  }, [productFormik.values, isSyndicated]);

  useEffect(() => {
    onChange &&
      onChange({
        applications: productFormik.values,
        manualTotalAmounts: !summarizeAmounts,
        requestedAmounts: manualAmounts,
        is_syndicated: isSyndicated,
      });
  }, [manualAmounts, summarizeAmounts, isSyndicated]);

  useEffect(() => {
    setIsSyndicated(!!isSyndicatedProp);
  }, [isSyndicatedProp]);

  const handleSyndicatedToggle = (checked: boolean) => {
    if (!isApplicationEditable()) return;
    if (checked && !allowMarkSyndicated) return;
    if (checked) {
      setProductLinesSnapshot(productFormik.values.map((line) => ({ ...line, amount: { ...line.amount } })));
      const opLine = buildOperacionesPublicasLine(
        productFormik.values[0]?.amount
          ? { ...productFormik.values[0].amount }
          : undefined
      );
      productFormik.setValues([opLine]);
      setApplicationInfo([opLine]);
      setIsSyndicated(true);
      return;
    }
    const restored =
      productLinesSnapshot && productLinesSnapshot.length > 0
        ? productLinesSnapshot
        : [
            {
              id: 0,
              product_line_code: defaultSelectableProductLineCode(productLines ?? []),
              amount: { currency_code: localCurrencyCode, value: 0 },
              has_assets: false,
            },
          ];
    productFormik.setValues(restored);
    setApplicationInfo(restored);
    setProductLinesSnapshot(null);
    setIsSyndicated(false);
  };

  const addProduct = () => {
    if (isSyndicated) return;
    const defaultCode = pickDefaultAssetCode(managementRules, availableAssets);
    const useDefaultAsset = isQuotationsEnabled && !!defaultCode;

    const newProduct = {
      id: 0,
      product_line_code: defaultSelectableProductLineCode(productLines ?? []),
      amount: {
        currency_code: localCurrencyCode,
        value: 0,
      },
      has_assets: useDefaultAsset,
      asset_code: useDefaultAsset ? defaultCode : undefined,
    };

    productFormik.setValues([...productFormik.values, newProduct]);
    setApplicationInfo((prevData) => [...prevData, newProduct]);
  };

  const removeProduct = (index: number) => {
    setApplicationInfo((prevData) => prevData.filter((_, i) => i !== index));

    productFormik.setValues(productFormik.values.filter((_, i) => i !== index));
    updateTotals();
    setIsdeleteApplication(false);
  };

  const updateTotals = () => {
    const { local, usd } = sumApplicationTotalsByCurrency(
      productFormik.values,
      localCurrencyCode,
      CURRENCY_CODES.usd
    );

    setTotalArs(local);
    setTotalUsd(usd);
  };

  const isApplicationEditable = () => {
    return !isRejected && !isFinished && !isApproved && !isInCommittee && !isInInstrumentation;
  };

  const committeeProductsUnlocked =
    allowCommitteeEdit &&
    isInCommittee &&
    !isRejected &&
    !isFinished &&
    !isApproved &&
    !isInInstrumentation;

  const isProductsEditable = () =>
    (committeeProductsUnlocked || isApplicationEditable()) && !usdHistoricLocked;
  const isProductLineLocked = () => isSyndicated || !isProductsEditable();

  useEffect(() => {
    const fallbackCode = defaultSelectableProductLineCode(productLines ?? []);
    if (applications?.length > 0) {
      productFormik.setValues(applications);
    } else {
      const defaultProduct = {
        id: 0,
        product_line_code: fallbackCode,
        amount: {
          currency_code: localCurrencyCode,
          value: 0,
        },
      };

      productFormik.setValues([defaultProduct]);
      setApplicationInfo([defaultProduct]);
    }
    if (requestedAmounts.length > 0) {
      setManualAmounts(requestedAmounts);
    }

    if (manualTotalAmounts) {
      setsummarizeAmounts(!manualTotalAmounts);
    }
  }, [applications, productLines]);

  const handleInputValue = (
    summarizeAmounts: boolean,
    manualAmounts: amount[],
    currencyCode: string
  ) => {
    const amount = manualAmounts?.find(
      (item) => item.currency_code === currencyCode
    )?.value;

    if (amount === '' || amount === undefined) return '';

    if (typeof amount === 'string') {
      return toNumber(amount);
    }
    return amount;
  };

  const handleToggleChange = () => {
    if (summarizeAmounts) {
      setManualAmounts([
        {
          currency_code: localCurrencyCode,
          value: 0,
        },
        {
          currency_code: CURRENCY_CODES.usd,
          value: 0,
        },
      ]);
    }
    setsummarizeAmounts(!summarizeAmounts);
  };
  
  const handleSaveWithSignersFiles = async (data: any) => {
    try {
      setIsUploadingSignersFiles(true);
      
      // Si hay archivos pendientes de firmantes, subirlos primero
      if (signersUploadFunction) {
        await signersUploadFunction();
      }
      
      // Luego guardar los datos
      onSave(data);
    } catch (error) {
      console.error('Error al subir archivos de firmantes:', error);
      // Aquí podrías mostrar un toast de error
    } finally {
      setIsUploadingSignersFiles(false);
    }
  };

  const handleSignersChange = (e: {
    values: signersData[];
    hasErrors: boolean;
    hasPendingFiles?: boolean;
    uploadPendingFiles?: () => Promise<void>;
  }) => {
    setClientInfo((prev) => ({
      ...prev,
      signers: e.values,
    }));
    setHasErrors(e.hasErrors);
    
    // Guardar la función de subida de archivos si existe
    if (e.uploadPendingFiles) {
      setSignersUploadFunction(() => () => e.uploadPendingFiles!());
    }
  };
  return (
    <Card>
      <Modal
        isOpen={isDeleteApplicationOpen}
        onClose={() => setIsdeleteApplication(false)}
        title={'Eliminar producto'}
        primaryText='Eliminar'
        primaryVariant='danger'
        secondaryText='Cancelar'
        secondaryVariant='secondary'
        onSecondaryAction={() => {
          setIsdeleteApplication(false), setModalText('');
        }}
        onPrimaryAction={() => removeProduct(productToDelete)}
        children={
          <P type='1' variant='neutral'>
            ¿Estás seguro de que querés eliminar el producto?
          </P>
        }
      />
      {productLines && (
        <Card spacing={{ left: '6' }}>
          <P type='1' variant='neutral-1' weight='bold' modifier='strong'>
            Solicitud
          </P>
          {showSyndicatedCheckbox && (
            <Card margin={{ bottom: '3' }} display='grid' gap='8px'>
              <Checkbox
                id='application-syndicated-checkbox'
                checked={isSyndicated}
                disabled={
                  !isApplicationEditable() ||
                  (!allowMarkSyndicated && !isSyndicated)
                }
                size='md'
                onChange={(checked) => handleSyndicatedToggle(!!checked)}
              >
                Es sindicada
              </Checkbox>
              <P type='2' variant='neutral' withMargin={false}>
                {allowMarkSyndicated || isSyndicated
                  ? SYNDICATED_CHECKBOX_HELPER_TEXT
                  : LATE_SYNDICATION_HELPER_TEXT}
              </P>
            </Card>
          )}
          {usdHistoricLocked && (
            <Card
              display='flex'
              margin={{ bottom: '3' }}
              spacing='3'
              variant='warning'
              modifier='weak'
              gap='8px'
              borderRadiusAmount='4'
            >
              <Icon name='warning_triangle_outline' variant='warning' width={24} height={24} />
              <P type='2' variant='neutral' withMargin={false}>
                {USD_HISTORIC_LOCK_BANNER_TEXT}
              </P>
            </Card>
          )}
          {quotationWarningMessages.length > 0 && (
            <Card
              display='flex'
              margin={{ bottom: '3' }}
              spacing='3'
              variant='warning'
              modifier='weak'
              gap='8px'
              borderRadiusAmount='4'
              style={{ flexDirection: 'column' }}
            >
              {quotationWarningMessages.map((msg) => (
                <Card key={msg} display='flex' gap='8px' alignItems='center'>
                  <Icon name='warning_triangle_outline' variant='warning' width={24} height={24} />
                  <P type='2' variant='neutral' withMargin={false}>
                    {msg}
                  </P>
                </Card>
              ))}
            </Card>
          )}
          <form onSubmit={productFormik.handleSubmit}>
            {productFormik.values.map(
              (product: applicationData, index: number) => (
                <Card
                  key={product.id}
                  display='flex'
                  margin={{ bottom: '2' }}
                  alignItems='baseline'
                  width='100%'
                  gap='16px'
                >
                  <Card width={isQuotationsEnabled ? '35%' : '55%'} display='grid' gap='8px'>
                    {index === 0 && (
                      <P
                        type='2'
                        withMargin={false}
                        variant='neutral'
                        weight='bold'
                        modifier='strong'
                      >
                        Producto
                      </P>
                    )}
                    <DropDown
                      size='md'
                      id='application-product-line-dropdown'
                      items={productLines}
                      title={productLineDropdownTitle(
                        productLines,
                        productFormik.values[index].product_line_code
                      )}
                      onChange={(e) => {
                        const match = productLines.find((item) => item.label === e);
                        if (match) {
                          productFormik.setFieldValue(
                            `[${index}].product_line_code`,
                            match.code
                          );
                        }
                      }}
                      disabled={isProductLineLocked()}
                    />
                    <Span variant='danger' type='2'>
                      {productFormik.errors[index]?.product_line_code}
                    </Span>
                  </Card>
                  {isQuotationsEnabled && (
                    <>
                      <Card width='36%' display='grid' gap='8px'>
                        {index === 0 && (
                          <P
                            type='2'
                            withMargin={false}
                            variant='neutral'
                            weight='bold'
                            modifier='strong'
                          >
                            Activo
                          </P>
                        )}
                        <DropDown
                          size='md'
                          id='application-asset-dropdown'
                          items={[
                            {
                              label: ACTIVO_SIN_COTIZACION_LABEL,
                              code: '',
                              disabled: !isAssetOptionEnabled(
                                managementRules,
                                '',
                                availableAssets
                              ),
                            },
                            ...(Array.isArray(availableAssets)
                              ? filterActivoSelectableAssets(availableAssets)
                                  .filter((asset: any) => {
                                    if (
                                      managementRules &&
                                      !managementRules.assetCodes.includes(asset.code)
                                    ) {
                                      return false;
                                    }
                                    return true;
                                  })
                                  .map((asset: any) => ({
                                    label: asset.name,
                                    code: asset.code,
                                    disabled: !isAssetOptionEnabled(
                                      managementRules,
                                      asset.code,
                                      availableAssets
                                    ),
                                  }))
                              : []),
                          ]}
                          title={
                            Array.isArray(availableAssets) ? 
                              (availableAssets.find(
                                (item: any) =>
                                  item.code === productFormik.values[index].asset_code
                              )?.name || ACTIVO_SIN_COTIZACION_LABEL) : ACTIVO_SIN_COTIZACION_LABEL
                          }
                          onChange={(e) => {
                            if (e === ACTIVO_SIN_COTIZACION_LABEL) {
                              // Reset asset fields
                              productFormik.setFieldValue(
                                `[${index}].asset_code`,
                                undefined
                              );
                              productFormik.setFieldValue(
                                `[${index}].has_assets`,
                                false
                              );
                              productFormik.setFieldValue(
                                `[${index}].asset_count`,
                                undefined
                              );
                              productFormik.setFieldValue(
                                `[${index}].amount.value`,
                                0
                              );
                            } else {
                              const selectedAsset = Array.isArray(availableAssets) ? 
                                availableAssets.find((item: any) => item.name === e) : null;
                              if (selectedAsset) {
                                productFormik.setFieldValue(
                                  `[${index}].asset_code`,
                                  selectedAsset.code
                                );
                                productFormik.setFieldValue(
                                  `[${index}].has_assets`,
                                  true
                                );
                                productFormik.setFieldValue(
                                  `[${index}].amount.currency_code`,
                                  localCurrencyCode
                                );
                                // Auto-calculate amount if asset_count exists
                                if (productFormik.values[index].asset_count && hasQuotatedSaleValue(selectedAsset.saleValue)) {
                                  const calculatedAmount =
                                    selectedAsset.saleValue *
                                    productFormik.values[index].asset_count;
                                  productFormik.setFieldValue(
                                    `[${index}].amount.value`,
                                    calculatedAmount
                                  );
                                } else if (!hasQuotatedSaleValue(selectedAsset.saleValue)) {
                                  productFormik.setFieldValue(
                                    `[${index}].amount.value`,
                                    0
                                  );
                                }
                              }
                            }
                          }}
                          disabled={!isProductsEditable()}
                        />
                        <Span variant='danger' type='2'>
                          {productFormik.errors[index]?.asset_code}
                        </Span>
                      </Card>
                      <Card width='20%' display='grid' gap='8px'>
                        {index === 0 && (
                          <P
                            type='2'
                            withMargin={false}
                            variant='neutral'
                            weight='bold'
                            modifier='strong'
                          >
                            Cantidad
                          </P>
                        )}
                        <Card margin={{ top: '2' }}>
                          <Input
                            id='application-asset-count-input'
                            type='number'
                            value={productFormik.values[index].asset_count?.toString() || ''}
                            onChange={(e) => {
                              const count = parseInt(e.target.value) || 0;
                              productFormik.setFieldValue(
                                `[${index}].asset_count`,
                                count
                              );
                              // Auto-calculate amount if asset is selected
                              const selectedAsset = Array.isArray(availableAssets) ?
                                availableAssets.find(
                                  (item: any) =>
                                    item.code === productFormik.values[index].asset_code
                                ) : null;
                              if (selectedAsset && count > 0 && hasQuotatedSaleValue(selectedAsset.saleValue)) {
                                const calculatedAmount =
                                  selectedAsset.saleValue * count;
                                productFormik.setFieldValue(
                                  `[${index}].amount.value`,
                                  calculatedAmount
                                );
                              } else if (selectedAsset && !hasQuotatedSaleValue(selectedAsset.saleValue)) {
                                productFormik.setFieldValue(
                                  `[${index}].amount.value`,
                                  0
                                );
                              }
                            }}
                            disabled={
                              !isProductsEditable() ||
                              !productFormik.values[index].asset_code
                            }
                          />
                        </Card>
                        <Span variant='danger' type='2'>
                          {productFormik.errors[index]?.asset_count}
                        </Span>
                      </Card>
                    </>
                  )}
                  <Card display='flex' gap='16px' width='65%'>
                    <Card width='30%' display='grid' gap='8px'>
                      {index === 0 && (
                        <P
                          type='2'
                          withMargin={false}
                          variant='neutral'
                          weight='bold'
                          modifier='strong'
                        >
                          Moneda
                        </P>
                      )}
                      {!isUruguayEnabled &&
                      isQuotationsEnabled &&
                      !!productFormik.values[index].asset_code ? (
                        <Card margin={{ top: '2' }}>
                          <Input
                            id='application-currency-input'
                            value={productFormik.values[index].amount.currency_code}
                            disabled={true}
                          />
                        </Card>
                      ) : (
                        <DropDown
                          id='application-currency-dropdown'
                          items={
                            managementRules
                              ? [
                                  { label: localCurrencyCode },
                                  {
                                    label: CURRENCY_CODES.usd,
                                    disabled: !currencyUsdAvailable,
                                  },
                                ]
                              : buildOriginationCurrencyDropdownItems(
                                  localCurrencyCode,
                                  CURRENCY_CODES.usd,
                                  currencyUsdAvailable
                                )
                          }
                          title={productFormik.values[index].amount.currency_code}
                          size='md'
                          onChange={(e) =>
                            productFormik.setFieldValue(
                              `[${index}].amount.currency_code`,
                              e
                            )
                          }
                          disabled={!isProductsEditable()}
                        />
                      )}
                      <Span variant='danger' type='2'>
                        {productFormik.errors[index]?.amount?.currency_code}
                      </Span>
                    </Card>
                    <Card width='100%'>
                      {index === 0 && (
                        <P
                          type='2'
                          withMargin={false}
                          variant='neutral'
                          weight='bold'
                          modifier='strong'
                        >
                          Monto
                        </P>
                      )}
                      <Card
                        display='flex'
                        margin={{ top: '3' }}
                        alignItems='center'
                        gap='16px'
                      >
                        <NumericFormat
                          inputStatus={
                            productFormik.errors[index]?.amount
                              ? 'error'
                              : 'normal'
                          }
                          placeholder='Ingresá un monto'
                          type='text'
                          disabled={
                            !isProductsEditable() ||
                            (isQuotationsEnabled &&
                              !!productFormik.values[index].asset_code)
                          }
                          id='application-amount-input'
                          value={String(
                            productFormik.values[index]?.amount.value
                          )}
                          maxLength={41}
                          thousandSeparator='.'
                          decimalSeparator=','
                          allowNegative={false}
                          onValueChange={(values) =>
                            productFormik.setFieldValue(
                              `[${index}].amount.value`,
                              values.floatValue ?? ''
                            )
                          }
                          customInput={Input}
                          decimalScale={2}
                        />
                        {index !== 0 && (
                          <Button
                            size='md'
                            variant='secondary'
                            onClick={() => {
                              setIsdeleteApplication(true),
                                setProductToDelete(index);
                            }}
                            disabled={!isProductsEditable()}
                          >
                            <Card
                              variant='none'
                              margin={{
                                top: '2',
                                left: '0',
                                bottom: '2',
                                right: '0',
                              }}
                            >
                              <Icon
                                variant='neutral'
                                name='delete_outline'
                                cursor='pointer'
                              />
                            </Card>
                          </Button>
                        )}
                      </Card>
                      <Span variant='danger' type='2'>
                        {productFormik.errors[index]?.amount}
                      </Span>
                    </Card>
                  </Card>
                </Card>
              )
            )}
            <Button
              id='application-add-button'
              variant='tertiary'
              size='md'
              onClick={addProduct}
              disabled={
                isSyndicated ||
                !productFormik.isValid ||
                !isProductsEditable()
              }
            >
              + Agregar solicitud
            </Button>
          </form>
          <Card display='flex' justifyContent='flex-end' alignItems='center'>
            <Toggle
              id='application-toggle-amounts'
              checked={summarizeAmounts}
              leftText='Ingresar monto total'
              rightText='Sumar montos'
              onChange={() => handleToggleChange()}
              disabled={!isProductsEditable()}
            />
          </Card>
          <Card
            border={true}
            display='flex'
            variant={clientTheme ? 'primary' : 'success'}
            modifier='weak'
            spacing={'3'}
            justifyContent='space-between'
            alignItems='center'
          >
            <Card width='70%'>
              <H type='3'>Total a solicitar:</H>
            </Card>

            {summarizeAmounts ? (
              <Card width='30%'>
                <H type='3' withMargin={false} weight='bold' variant={clientTheme ? 'primary' : 'success'}>
                  {localCurrencyCode} {formatCurrency(totalArs ?? 0, true)}
                </H>

                <H type='3' withMargin={false} weight='bold' variant={clientTheme ? 'primary' : 'success'}>
                  {CURRENCY_CODES.usd} {formatCurrency(totalUsd ?? 0, true)}
                </H>
              </Card>
            ) : (
              <Card width='30%'>
                <Card display='flex' gap='8px'>
                  <H
                    type='3'
                    withMargin={false}
                    weight='bold'
                    variant={clientTheme ? 'primary' : 'success'}
                  >
                    {localCurrencyCode}{' '}
                  </H>
                  <NumericFormat
                    placeholder='Ingresá un monto'
                    type='text'
                    disabled={!isProductsEditable()}
                    id='application-local-currency-input'
                    value={handleInputValue(
                      summarizeAmounts,
                      manualAmounts,
                      localCurrencyCode
                    )}
                    variant='transparent'
                    maxLength={41}
                    thousandSeparator='.'
                    decimalSeparator=','
                    onValueChange={(values) =>
                      handleInputChange(
                        localCurrencyCode,
                        values.floatValue ?? ''
                      )
                    }
                    customInput={Input}
                    decimalScale={2}
                    allowNegative={false}
                  />
                </Card>
                <Card display='flex' gap='8px'>
                  <H
                    type='3'
                    withMargin={false}
                    weight='bold'
                    variant={clientTheme ? 'primary' : 'success'}
                  >
                    {CURRENCY_CODES.usd}{' '}
                  </H>
                  <NumericFormat
                    placeholder='Ingresá un monto'
                    type='text'
                    disabled={!isProductsEditable() || (!isUruguayEnabled && !usdAvailable)}
                    id='application-usd-input'
                    value={handleInputValue(
                      summarizeAmounts,
                      manualAmounts,
                      CURRENCY_CODES.usd
                    )}
                    variant='transparent'
                    maxLength={41}
                    thousandSeparator='.'
                    decimalSeparator=','
                    onValueChange={(values) =>
                      handleInputChange(
                        CURRENCY_CODES.usd,
                        values.floatValue ?? ''
                      )
                    }
                    customInput={Input}
                    decimalScale={2}
                    allowNegative={false}
                  />
                </Card>
              </Card>
            )}
          </Card>
          {!hideInfo && (
            <Card margin={{ top: '5' }}>
              {!isUruguayEnabled && (
                <>
                  <P type='1' variant='neutral-1' weight='bold' modifier='strong'>
                    Firmantes
                  </P>
                  <Signers
                    cuit={cuit}
                    signersData={signers || []}
                    isApplicationEditable={!isApplicationEditable()}
                    onChange={handleSignersChange}
                    applicationId={id}
                    corporateStructure={clientInfo.corporate_structure}
                    requireValidations={!isApplicationEditable()}
                    allowEmptySigners={true}
                  />
                </>
              )}
              <P type='1' variant='neutral-1' weight='bold' modifier='strong'>
                Datos complementarios
              </P>

              {/* Fila 1: Fecha de vigencia y Clase de crédito */}
              {(isApplicationEffectiveDateEnabled || isApplicationCreditTypeEnabled) && (
                <Card display='flex' justifyContent='space-between' gap='16px' margin={{ top: '3' }}>
                  {isApplicationEffectiveDateEnabled && (
                    <Card margin={{ top: '3' }} width={isApplicationCreditTypeEnabled ? '50%' : '100%'}>
                      <P withMargin={false} variant='neutral' type='4' modifier='strong'>
                        Fecha de vigencia
                      </P>
                      <Card margin={{ top: '2' }}>
                        <DatePicker
                          defaultValue={applicationDataFormik.values.effective_date || ''}
                          onChange={(date) => {
                            applicationDataFormik.setFieldValue('effective_date', date);
                          }}
                          disabled={!isApplicationEditable()}
                          displayFormat="DD/MM/AA"
                          minDate={new Date()}
                        />
                      </Card>
                    </Card>
                  )}
                  {isApplicationCreditTypeEnabled && (
                    <Card width={isApplicationEffectiveDateEnabled ? '50%' : '100%'}>
                      <DropDown
                        id='application-credit-type-dropdown'
                        label='Clase de crédito *'
                        title={getSelectedCreditType()}
                        items={creditTypeItems}
                        onChange={handleCreditTypeChange}
                        size="md"
                        marginTop="4px"
                        disabled={!isApplicationEditable()}
                      />
                    </Card>
                  )}
                </Card>
              )}
              
              {/* Fila 2: Motivo y Comentarios */}
              <Card display='flex' justifyContent='space-between' gap='16px' margin={{ top: '3' }}>
                <Card width='50%'>
                  <Input
                    id='application-reason-input'
                    onChange={(e) =>
                      applicationDataFormik.setFieldValue(
                        'reason',
                        e.target.value
                      )
                    }
                    label='Motivo'
                    placeholder='Ingresá el motivo de la solicitud'
                    value={applicationDataFormik.values.reason}
                    disabled={!isApplicationEditable()}
                  />
                </Card>
                <Card width='50%'>
                  <Input
                    id='application-extra-comment-input'
                    value={applicationDataFormik.values.extraComment}
                    onChange={(e) =>
                      applicationDataFormik.setFieldValue(
                        'extraComment',
                        e.target.value
                      )
                    }
                    label='Comentarios'
                    placeholder='Comentarios adicionales'
                    disabled={!isApplicationEditable()}
                  />
                </Card>
              </Card>
              
              {/* Fila 3: Reseñas (full width) */}
              <Card margin={{ top: '3' }} width='100%'>
                <TextArea
                  id='application-review-input'
                  maxLength={1000}
                  value={applicationDataFormik.values.review}
                  onChange={(e) =>
                    applicationDataFormik.setFieldValue(
                      'review',
                      e.target.value
                    )
                  }
                  placeHolder='Ingresá una breve reseña de la empresa'
                  label='Reseñas'
                  disabled={!isApplicationEditable()}
                />
              </Card>
            </Card>
          )}
          {!hideInfo && (
            <Card
              display='flex'
              justifyContent='space-between'
              margin={{ top: '5' }}
            >
              <Card width='200px'>
                <Button
                  id='client-button-back'
                  size='lg'
                  variant='secondary'
                  onClick={() => onPreviousClick()}
                  fullWidth
                >
                  Volver
                </Button>
              </Card>
              <Card
                display='flex'
                justifyContent='flex-end'
                gap='16px'
                width='420px'
              >
                <Button
                  id='client-button-save'
                  size='lg'
                  variant='secondary'
                  onClick={() =>
                    handleSaveWithSignersFiles({
                      applications: productFormik.values,
                      manualTotalAmounts: !summarizeAmounts,
                      requestedAmounts: manualAmounts,
                      reason: applicationDataFormik.values.reason,
                      review: applicationDataFormik.values.review,
                      extraComment: applicationDataFormik.values.extraComment,
                      corporateStructure: clientInfo.corporate_structure,
                      signers: clientInfo.signers,
                      effective_date: applicationDataFormik.values.effective_date,
                      credit_type_id: applicationDataFormik.values.credit_type_id,
                      credit_type_name: applicationDataFormik.values.credit_type_name,
                      is_syndicated: isSyndicated,
                    })
                  }
                  disabled={
                    isRejectedStatus(applicationStatus) ||
                    isApprovedStatus(applicationStatus) ||
                    isFinishedStatus(applicationStatus) ||
                    isCommitteeStatus(applicationStatus) ||
                    isInstrumentationStatus(applicationStatus) ||
                    isSaving ||
                    isUploadingSignersFiles ||
                    signersHasErrors ||
                    hasQuotationConfigErrors
                  }
                  fullWidth
                >
                  {isSaving || isUploadingSignersFiles ? (
                    <Spinner size='sm' variant='primary' />
                  ) : (
                    'Guardar'
                  )}
                </Button>
                <Button
                  id='client-button-continue'
                  size='lg'
                  variant='primary'
                  onClick={() =>
                    handleSaveWithSignersFiles({
                      applications: productFormik.values,
                      shouldContinue: signersHasErrors === false,
                      manualTotalAmounts: !summarizeAmounts,
                      requestedAmounts: manualAmounts,
                      reason: applicationDataFormik.values.reason,
                      review: applicationDataFormik.values.review,
                      extraComment: applicationDataFormik.values.extraComment,
                      corporateStructure: clientInfo.corporate_structure,
                      signers: clientInfo.signers,
                      effective_date: applicationDataFormik.values.effective_date,
                      credit_type_id: applicationDataFormik.values.credit_type_id,
                      credit_type_name: applicationDataFormik.values.credit_type_name,
                      is_syndicated: isSyndicated,
                    })
                  }
                  fullWidth
                  disabled={
                    !productFormik.isValid || 
                    isSaving || 
                    isUploadingSignersFiles || 
                    (signersHasErrors && clientInfo.signers.length > 0) ||
                    hasQuotationConfigErrors ||
                    (isApplicationCreditTypeEnabled && !applicationDataFormik.values.credit_type_id && !applicationDataFormik.values.credit_type_name)
                  }
                >
                  {isSaving || isUploadingSignersFiles ? (
                    <Spinner size='sm' variant='primary' />
                  ) : (
                    'Continuar'
                  )}
                </Button>
              </Card>
            </Card>
          )}
        </Card>
      )}
    </Card>
  );
};

export default Application;
